import { ClosSession } from '@apps/clos-sessions/state/remote-sessions/types';

/** How the backend spells a participant's role in media tokens and in `foto_room.control` envelopes */
export type ClosMediaRoleDTO = 'photograph' | 'model' | 'observer';

/**
 * The LiveKit descriptor the backend issues with create, join and `media-token` answers.
 * It is absent for Twilio rooms and never part of `GET /v1/room/{roomId}`.
 */
export interface ClosSessionMediaDTO {
  /** The only provider the backend issues a descriptor for */
  provider: 'livekit';

  /** LiveKit signalling URL */
  url: string;

  /** LiveKit access token */
  token: string;

  /** LiveKit room name: `foto-room-<roomId>-<mediaSessionId>`, not the CLOS room id */
  room: string;

  media_session_id: string;

  /** The same client session keeps the participant identity across token refreshes */
  client_session_id: string;

  /** `<user_id>:<client_session_id>` */
  participant_identity: string;

  participant_role: ClosMediaRoleDTO;

  can_publish: boolean;

  can_subscribe: boolean;

  can_publish_data: boolean;

  /** RFC 3339 */
  expires_at: string;
}

export interface ClosSessionDTO {
  _id: string;

  name: string;

  /** Sessions are always of type "standard" */
  type: 'standard';

  owner_id: string;

  owner_name: string;

  /** Empty string if the role hasn't been filled yet */
  photograph_id: string;

  /** Empty string if the role hasn't been filled yet */
  model_id: string;

  /** Consists of observers IDs */
  participants: string[];

  status: string;

  /** It's always empty string during update phase (GET /v1/room/{roomId}) */
  token: string;

  /** Present only when the room's media runs on LiveKit, and only in create and join answers */
  media?: ClosSessionMediaDTO;

  links_by_role: {
    photograph?: string;
    model?: string;
    observer: string;
  };

  invite_links?: {
    generic: string;
    by_role?: {
      photograph?: string;
      model?: string;
      observer?: string;
    };
  };

  link: string;

  /** @deprecated - use {@link ClosSessionDTO.link} instead */
  deeplink: string;

  is_cloud_images: boolean;

  is_available_for_all: boolean;

  clos_cloud_images_count: number;

  dropbox_images_count: number;

  local_images_count: number;

  inner_gallery_images_count: number;

  is_paid_owner: boolean;

  avatars: { [participantId: string]: string } | null;

  users: { [participantId: string]: ClosSessionParticipantDTO } | null;
}

export enum ClosSessionRoleDTO {
  observer = 'observer',
  model = 'model',
  photographer = 'photograph',
}

export interface ClosSessionParticipantDTO {
  _id: string;

  name: string;

  /** Contains avatar url or an empty string if the user doesn't have their pic */
  avatar: string;
}

export interface CreateSessionInput {
  useCloud: boolean;

  public: boolean;

  name: string;

  role: ClosSessionRoleDTO;

  /** ISO string */
  date: string;

  guid: string;
}

export interface JoinSessionInput {
  sessionId: string;

  role: ClosSessionRoleDTO;
}

export interface GetSessionInput {
  sessionId: string;
}

/**
 * The update phase (GET /v1/room/{roomId}) carries no credentials, so a refresh
 * can never overwrite the stored Twilio token or LiveKit descriptor
 */
export type GetSessionOutput = Omit<ClosSession, 'roomToken' | 'media'>;
